import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Aba } from '../../../components/Abas'

/**
 * As seções do detalhe, e qual delas está aberta.
 *
 * A aba aberta mora na URL (`?aba=historico`) pelo mesmo motivo dos filtros da lista: o link
 * tem de levar a pessoa exatamente para onde quem mandou estava olhando. Quem cola no e-mail
 * "confere a trilha desta GMUD" precisa que o destinatário caia no histórico, não na primeira
 * aba com a instrução "agora clique em Histórico".
 */

/** Nome do parâmetro. Curto porque ele aparece no link que se cola no e-mail. */
const PARAMETRO = 'aba'

/**
 * As seções que existem hoje.
 *
 * `id` não é rótulo: o id vai para a URL e para os links já compartilhados, então renomear a
 * aba na interface (Regra nº 4 — é só mudar uma string) não pode quebrar um link antigo.
 *
 * "Comprovações" vem logo depois do plano, e não no fim: é a aba que decide se dá para encerrar a
 * solicitação, e a última posição é onde se põe o que ninguém precisa abrir.
 */
export const ABAS_DO_DETALHE = [
  { id: 'plano', rotulo: 'Plano da mudança' },
  { id: 'anexos', rotulo: 'Comprovações' },
  { id: 'classificacao', rotulo: 'Classificação e risco' },
  { id: 'historico', rotulo: 'Histórico' },
] as const

export type IdDeAba = (typeof ABAS_DO_DETALHE)[number]['id']

/** Onde a tela abre quando o endereço não pede nada. */
export const ABA_PADRAO: IdDeAba = 'plano'

function ehAba(valor: string): valor is IdDeAba {
  return ABAS_DO_DETALHE.some((aba) => aba.id === valor)
}

/**
 * As abas com o contador de pendências no plano.
 *
 * O número fica na aba, e não só dentro dela, porque "falta preencher" é a informação que
 * decide se vale abrir a seção — e o plano é justamente a aba que a pessoa deixa para depois.
 *
 * @param pendencias quantos itens do plano ainda faltam, como o servidor os conta
 * @returns as abas prontas para o componente `Abas`
 */
export function abasDoDetalhe(pendencias: number): Aba[] {
  return ABAS_DO_DETALHE.map((aba) => ({
    id: aba.id,
    rotulo: aba.rotulo,
    contador: aba.id === 'plano' && pendencias > 0 ? pendencias : undefined,
  }))
}

/**
 * Lê e escreve a aba aberta na URL.
 *
 * Aba desconhecida cai na padrão em vez de mostrar painel vazio: link antigo, erro de digitação
 * e aba que deixou de existir dão todos no mesmo lugar, e nenhum deles vira tela em branco.
 *
 * Troca a entrada do histórico em vez de empilhar (`replace`), ao contrário dos filtros da lista.
 * A diferença é intencional: filtrar é refazer a pergunta, e desfazer o filtro é útil; abrir uma
 * aba é olhar o mesmo registro por outro lado, e quem aperta "voltar" depois de passar pelas três
 * quer sair para a lista, não caminhar de aba em aba até chegar lá.
 *
 * @returns a aba corrente e a função que a troca
 */
export function useAbaNaUrl(): { aba: IdDeAba; abrir: (id: string) => void } {
  const [parametros, definirParametros] = useSearchParams()
  const pedida = parametros.get(PARAMETRO) ?? ''

  const abrir = useCallback(
    (id: string) => {
      const proximos = new URLSearchParams(parametros)
      proximos.set(PARAMETRO, id)
      definirParametros(proximos, { replace: true })
    },
    [parametros, definirParametros],
  )

  return { aba: ehAba(pedida) ? pedida : ABA_PADRAO, abrir }
}
